/**
 * Copy context — exposes the active label overrides + per-user copy prefs.
 *
 * Populated by `<ThemeProvider>` so every consumer of the brand theme also
 * gets access to copy without a second provider in the tree.
 */
import * as React from "react";
import type { LabelOverrides } from "@/theme/types";
import { DEFAULT_COPY_PREFS, type CopyPrefs } from "@/theme/copy/prefs";

export interface CopyContextValue {
  labels: LabelOverrides;
  prefs: CopyPrefs;
  setPrefs: (next: CopyPrefs) => void;
  /**
   * When true, *any* themed override on the current page should be revealed
   * (toggled by the `?` keyboard shortcut). Independent of `prefs.showRevealHints`.
   */
  forceRevealAll: boolean;
  setForceRevealAll: (next: boolean) => void;
}

export const CopyContext = React.createContext<CopyContextValue>({
  labels: {},
  prefs: DEFAULT_COPY_PREFS,
  setPrefs: () => {},
  forceRevealAll: false,
  setForceRevealAll: () => {},
});

export function useCopyContext(): CopyContextValue {
  return React.useContext(CopyContext);
}
